import type { ParsedValue, ParsedClasses, EditorBreakpoint, PropertyValue } from './types';
import { EDITOR_BREAKPOINTS, isPropertyValue } from './types';

/**
 * Helper to safely get a PropertyValue from ParsedClasses.
 * Returns an empty object if the property doesn't exist or isn't a PropertyValue.
 */
function getPropertyValue(parsed: ParsedClasses, property: string): PropertyValue {
  const value = parsed[property];
  return isPropertyValue(value) ? value : {};
}

function getBreakpointIndex(breakpoint: EditorBreakpoint): number {
  return EDITOR_BREAKPOINTS.indexOf(breakpoint);
}

function getLowerBreakpoint(breakpoint: EditorBreakpoint): EditorBreakpoint {
  const index = getBreakpointIndex(breakpoint);
  const lower = index > 0 ? EDITOR_BREAKPOINTS[index - 1] : undefined;
  return lower ?? 'base';
}

function getFallbackValue(propertyValue: PropertyValue, breakpoint: EditorBreakpoint): ParsedValue | undefined {
  for (let i = getBreakpointIndex(breakpoint); i >= 0; i -= 1) {
    const breakpointAtIndex = EDITOR_BREAKPOINTS[i];
    if (breakpointAtIndex) {
      const value = propertyValue[breakpointAtIndex];
      if (value) {
        return value;
      }
    }
  }
  return undefined;
}

function hasAnyValue(propertyValue: PropertyValue): boolean {
  return EDITOR_BREAKPOINTS.some((breakpoint) => Boolean(propertyValue[breakpoint]));
}

/**
 * Compare two ParsedValues for semantic equality.
 *
 * Note: This intentionally ignores `breakpoint` and `normalizedValue` when comparing.
 * - `breakpoint` is excluded because this function is used for diff-based logic
 *   (e.g., comparing a base value to an lg value to determine if they're equivalent)
 * - `normalizedValue` is excluded because it's derived from `value` and `type`
 *
 * Values are considered equal if they have the same property, value, type, and modifier.
 */
export function valuesAreEqual(a?: ParsedValue, b?: ParsedValue): boolean {
  if (!a || !b) return a === b;
  return a.property === b.property && a.value === b.value && a.type === b.type && a.modifier === b.modifier;
}

export function getEffectiveValue({
  parsed,
  property,
  breakpoint,
}: {
  parsed: ParsedClasses;
  property: string;
  breakpoint: EditorBreakpoint;
}): ParsedValue | undefined {
  const propertyValue = getPropertyValue(parsed, property);
  if (!hasAnyValue(propertyValue)) {
    return undefined;
  }

  return getFallbackValue(propertyValue, breakpoint);
}

export function getEffectiveValues({
  parsed,
  breakpoint,
}: {
  parsed: ParsedClasses;
  breakpoint: EditorBreakpoint;
}): Record<string, ParsedValue> {
  const result: Record<string, ParsedValue> = {};

  for (const [property] of Object.entries(parsed)) {
    if (property !== 'unknown') {
      const effective = getEffectiveValue({ parsed, property, breakpoint });
      if (effective) {
        result[property] = effective;
      }
    }
  }

  return result;
}

/**
 * Set a value in ParsedClasses. Property and breakpoint are extracted from the value.
 * Implements diff-based logic: if setting lg to same as base, removes lg override.
 */
export function setValue(parsed: ParsedClasses, value: ParsedValue): ParsedClasses {
  const { property, breakpoint } = value;
  const newParsed = { ...parsed };
  const currentPropertyValue = getPropertyValue(parsed, property);

  if (breakpoint === 'base') {
    newParsed[property] = { ...currentPropertyValue, base: value };
    return newParsed;
  }

  const lowerEffective = getFallbackValue(currentPropertyValue, getLowerBreakpoint(breakpoint));
  if (valuesAreEqual(value, lowerEffective)) {
    const updated = { ...currentPropertyValue };
    delete updated[breakpoint];
    if (hasAnyValue(updated)) {
      newParsed[property] = updated;
    } else {
      delete newParsed[property];
    }
    return newParsed;
  }

  newParsed[property] = { ...currentPropertyValue, [breakpoint]: value };

  return newParsed;
}

/**
 * Set a value with "preserve desktop" behavior.
 * When setting base and there's no lg override, the old base value is promoted to lg.
 * This preserves the desktop experience when editing mobile.
 *
 * Use this for properties where values are mutually exclusive modes (e.g., display: flex/grid/hidden).
 *
 * @example
 * // Start: grid (base only - both mobile and desktop show grid)
 * // Set base to hidden
 * // Result: hidden lg:grid (mobile hidden, desktop still shows grid)
 */
export function setValuePreservingDesktop(parsed: ParsedClasses, value: ParsedValue): ParsedClasses {
  const { property, breakpoint } = value;
  const newParsed = { ...parsed };
  const currentPropertyValue = getPropertyValue(parsed, property);

  if (breakpoint !== 'base') {
    return setValue(parsed, value);
  }

  // If there's a base value but no lg override, promote old base to lg first
  if (currentPropertyValue.base && !currentPropertyValue.lg && !valuesAreEqual(currentPropertyValue.base, value)) {
    const promotedLg: ParsedValue = { ...currentPropertyValue.base, breakpoint: 'lg' };
    newParsed[property] = { ...currentPropertyValue, base: value, lg: promotedLg };
  } else {
    newParsed[property] = { ...currentPropertyValue, base: value };
  }

  return newParsed;
}

/**
 * Clear a value at a specific breakpoint.
 * - Clearing base: removes base value, keeps lg if present
 * - Clearing lg: removes lg override, falls back to base
 */
export function clearValue(parsed: ParsedClasses, property: string, breakpoint: EditorBreakpoint): ParsedClasses {
  const newParsed = { ...parsed };
  const currentPropertyValue = getPropertyValue(parsed, property);

  const updated = { ...currentPropertyValue };
  delete updated[breakpoint];

  if (hasAnyValue(updated)) {
    newParsed[property] = updated;
  } else {
    delete newParsed[property];
  }

  return newParsed;
}
